import React, { useEffect, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Statistic,
  StatisticProps,
  Table,
  Select,
} from "antd";
import dayjs from "dayjs";
import {
  ArrowUpOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  endOfDay,
  endOfMonth,
  format,
  getMonth,
  getYear,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";

import { useTranslation } from "react-i18next";
import { calculateMonthlyHours } from "../../utils";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import "./UserPayment.scss";
import SalarySection from "./Components/SalarySection/SalarySection";
import { getUserWorkLocationsHelper } from "../../../../utils/helpers/workLocationsHelper";
import {
  useGetUserPaymentsQuery,
  useGetSalaryHistoryQuery,
  useGetUserDetailsPaymentsQuery,
} from "../../../../utils/redux/api/financeApi";
import FixedPayment from "../../../../Components/Modals/UserPayments/FixedPayment/FixedPayment";
import PercentageSalary from "../../../../Components/Modals/UserPayments/PercentageSalary/PercentageSalary";
import BonusPayment from "../../../../Components/Modals/UserPayments/BonusPayment/BonusPayment";
import TravelExpensesModal from "../../../../Components/Modals/UserPayments/TravelExpenses/TravelExpenses";
import PenaltyPayment from "../../../../Components/Modals/UserPayments/PenaltyPayment/PenaltyPayment";
import OvertimePayment from "../../../../Components/Modals/UserPayments/OvertimePayment/OvertimePayment";
import LoanPaymentModal from "../../../../Components/Modals/UserPayments/LoanPaymentModal/LoanPaymentModal";
import { message } from "antd";
import ShortageOverageModal from "../../../../Components/Modals/UserPayments/ShortageOverageModal/ShortageOverageModal";
import UserHourlyRatePercentageModal from "../../../../Components/Modals/UserHourlyPercentageRateModal/UserHourlyRatePercentageRateModal";
const { RangePicker } = DatePicker;

type Props = {
  userDetails: any;
  onChangeDate: (date: any) => void;
  onUpdateUser?: () => void;
};
interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

type Entry = {
  status: string;
  _id: string;
  userId: string;
  from: string;
  to: string;
  duration: number; // duration u minutama
  hourly_rate: number;
  pay_multiplier: number;
};

const UserPayment: React.FC<Props> = ({ userDetails, onChangeDate, onUpdateUser }) => {
  const [selectedMonth, setSelectedMonth] = useState<any>(dayjs(new Date()));
  const [penaltyModalOpen, setPenaltyModalOpen] = useState<any>(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const [shortageOverageModalOpen, setShortageOverageModalOpen] =
    useState(false);

  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([dayjs(startOfMonth(new Date())), dayjs(endOfMonth(new Date()))]);
  const [loanPaymentModalOpen, setLoanPaymentModalOpen] =
    useState<boolean>(false);

  const [fixedPaymentModalOpen, setFixedPaymentModalOpen] =
    useState<any>(false);
  const [percentageSalaryModalOpen, setPercentageSalaryModalOpen] =
    useState<boolean>(false);
  const [bonusPaymentModalOpen, setBonusPaymentModalOpen] =
    useState<boolean>(false);
  const [overtimePaymentModalOpen, setOvertimePaymentModalOpen] =
    useState<boolean>(false);

  const [isDailyWageTableVisible, setDailyWageTableVisible] =
    useState<boolean>(false);
  const [isPercentageSalaryTableVisible, setIsPercentageSalaryTableVisible] =
    useState<boolean>(false);

  const [isTravelExpensesModalOpen, setTravelExpensesModalOpen] =
    useState<boolean>(false);
  const [isUserSalaryModalOpen, setUserSalaryModalOpen] =
    useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSegment, setActiveSegment] = useState<'monthly' | 'first-half' | 'second-half' | 'custom'>('monthly');

  const [totalEarned, setTotalErned] = useState<any>(0);
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(true);
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >(undefined);

  const userData = useSelector((state: any) => state.userData.user);
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const dispatch = useDispatch();
  const params = useParams();
  const { t } = useTranslation();

  const paymentDetails: any = userDetails?.patientDetails
    ? ([]?.concat(
        ...userDetails?.patientDetails?.map((item: any) => item.notes),
      ) ?? [])
    : [];
  useEffect(() => {
    setTotalErned(calculateTotalCosts(userDetails?.patientDetails));
  }, [userDetails]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadUserWorkLocations = async () => {
      if (!params?.id || !userData?.accessToken) {
        setIsLoadingLocations(false);
        return;
      }

      setIsLoadingLocations(true);
      try {
        const locations = await getUserWorkLocationsHelper(
          params.id,
          userData.accessToken,
          dispatch,
          workLocationsState,
          false,
        );
        setUserWorkLocations(locations || []);

        // Keep selectedLocationId as null by default to show "All Locations"
        // User can manually select a specific location if needed
      } catch (error) {
        console.error("Error loading user work locations:", error);
        setUserWorkLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    loadUserWorkLocations();
  }, [params?.id, userData?.accessToken]);

  // The period to fetch for — dateRange is the single source of truth after
  // mount (every handler below that changes the period updates dateRange),
  // falling back to the full selectedMonth only for the very first render.
  const clientId = userDetails?.user?.clientId?._id;
  const periodStart =
    dateRange?.[0]?.toDate() ?? startOfMonth(new Date(selectedMonth));
  const periodEnd =
    dateRange?.[1]?.toDate() ?? endOfMonth(new Date(selectedMonth));
  const canFetchPeriodData =
    !isLoadingLocations && !!clientId && !!params.id && !!periodStart && !!periodEnd;
  // The "All Locations" option passes value={null} (not undefined) — fold
  // both into undefined so the query param is omitted, same as the old
  // `selectedLocationId ? "&locationId=..." : ""` ternaries did.
  const locationIdParam = selectedLocationId || undefined;

  // Last 6 months salary history (bank + cash) for the sparkline
  const { data: salaryHistoryData } = useGetSalaryHistoryQuery(
    { clientId, userId: params.id as string, months: 6, locationId: locationIdParam },
    { skip: !clientId || !params.id || isLoadingLocations },
  );
  const salaryHistory = salaryHistoryData?.history ?? [];

  const { data: userOvertimePayment } = useGetUserDetailsPaymentsQuery(
    {
      userId: params.id as string,
      startDateStr: String(startOfDay(periodStart)),
      endDateStr: String(endOfDay(periodEnd)),
      locationId: locationIdParam,
    },
    { skip: !canFetchPeriodData },
  );

  const {
    data: employeeEarnings,
    refetch: refetchEmployeeEarnings,
  } = useGetUserPaymentsQuery(
    {
      clientId,
      userId: params.id as string,
      startDate: format(periodStart, "yyyy-MM-dd"),
      endDate: format(periodEnd, "yyyy-MM-dd"),
      locationId: locationIdParam,
    },
    { skip: !canFetchPeriodData },
  );

  // Kept as a no-arg wrapper so every onUpdate/onOvertimeSettingsChanged
  // callback below (unchanged call sites) still works the same way.
  const getEmployeeTotalEarning = () => {
    refetchEmployeeEarnings();
  };

  const disabledDate = (current: any) => {
    if (!selectedMonth) return false;
    const start = dayjs(startOfMonth(new Date(selectedMonth)));
    const end = dayjs(endOfMonth(new Date(selectedMonth)));
    return current < start || current > end;
  };

  const calculateTotalCosts = (data: any) => {
    let totalCosts = 0;
    // Loop through each patient's notes
    data?.forEach((patient: any) => {
      if (patient.notes) {
        patient?.notes?.forEach((note: any) => {
          // Add the costs to the total
          totalCosts += note.costs || 0;
        });
      }
    });
    return totalCosts;
  };

  const calculateCurrency = () => {
    if (userDetails?.user.clientId.currency === "Dinar") {
      return "RSD";
    } else if (userDetails?.user.clientId.currency === "Euro") {
      return "EUR";
    } else {
      return "$";
    }
  };
  const calculatePercentage = (value: any) => {
    const percentage = userDetails?.user?.amount ?? 30;
    return (percentage / 100) * value;
  };

  const columns: ColumnsType<DataType> = [
    {
      title: t("Costs"),
      dataIndex: "costs",
      key: "costs",
      render: (text) => (
        <>
          {text} {calculateCurrency()}
        </>
      ),
    },
    {
      title: t("Date"),
      dataIndex: "date",
      width: 150,
      key: "date",
      render: (value, row) => {
        return format(new Date(value), "d-MMM-yyyy");
      },
    },
    {
      title: t("Title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
    },
  ];

  // 🔹 Prev/Next sada pomjeraju mjesec i automatski resetuju range
  const handlePrevMonth = () => {
    const newMonth = selectedMonth.subtract(1, "month");
    setSelectedMonth(newMonth);
    setActiveSegment('monthly');
    setDateRange([
      dayjs(startOfMonth(new Date(newMonth.toDate()))),
      dayjs(endOfMonth(new Date(newMonth.toDate()))),
    ]);
    onChangeDate(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = selectedMonth.add(1, "month");
    setSelectedMonth(newMonth);
    setActiveSegment('monthly');
    setDateRange([
      dayjs(startOfMonth(new Date(newMonth.toDate()))),
      dayjs(endOfMonth(new Date(newMonth.toDate()))),
    ]);
    onChangeDate(newMonth);
  };

  // 🔹 Ova funkcija sprečava da se selektuju datumi iz dva mjeseca
  const onRangeChange = (values: (dayjs.Dayjs | null)[] | null) => {
    if (!values) return;

    const [start, end] = values;

    // Ako je izabran samo jedan datum, zapamti ga kao start
    if (start && !end) {
      setDateRange([start, null]);
      setSelectingStart(false);
      return;
    }

    // Ako su oba datuma izabrana
    if (start && end) {
      // Ako je korisnik kliknuo obrnut redosled, zamijeni ih
      const actualStart = end.isBefore(start, "day") ? end : start;
      const actualEnd = end.isBefore(start, "day") ? start : end;

      // Postavi validan range — earnings/overtime-payments/salary-history
      // queries all derive their args from dateRange, so this alone is
      // enough to trigger a fresh fetch for the new period.
      setDateRange([actualStart, actualEnd]);
      setSelectingStart(true);
    }
  };

  function calculateDetailedPayments(entries: Entry[]) {
    const groupedByMultiplier: Record<number, { hours: number; pay: number }> =
      {};
    let totalHours = 0;
    let totalPay = 0;

    entries?.forEach((entry) => {
      const hours = entry.duration / 60;
      const pay = hours * entry.hourly_rate * entry.pay_multiplier;

      if (!groupedByMultiplier[entry.pay_multiplier]) {
        groupedByMultiplier[entry.pay_multiplier] = { hours: 0, pay: 0 };
      }

      groupedByMultiplier[entry.pay_multiplier].hours += hours;
      groupedByMultiplier[entry.pay_multiplier].pay += pay;

      totalHours += hours;
      totalPay += pay;
    });

    const averageRate = totalHours > 0 ? totalPay / totalHours : 0;

    return {
      groupedByMultiplier,
      totalHours,
      totalPay,
      averageRate,
    };
  }

  const handleSegmentChange = (segment: 'monthly' | 'first-half' | 'second-half' | 'custom') => {
    setActiveSegment(segment);
    const base = selectedMonth.toDate();
    let start: Date;
    let end: Date;

    if (segment === 'monthly') {
      start = startOfMonth(base);
      end = endOfMonth(base);
    } else if (segment === 'first-half') {
      start = startOfMonth(base);
      end = new Date(base.getFullYear(), base.getMonth(), 15, 23, 59, 59);
    } else {
      start = new Date(base.getFullYear(), base.getMonth(), 16);
      end = endOfMonth(base);
    }

    // Queries derive their args from dateRange, so setting it is enough to
    // trigger a fresh fetch for the new segment.
    setDateRange([dayjs(start), dayjs(end)]);
  };

  // Derived display values for toolbar
  const rangeStart = dateRange?.[0]?.format('DD.MM.YYYY') ?? '—';
  const rangeEnd   = dateRange?.[1]?.format('DD.MM.YYYY') ?? '—';
  const monthLabel = selectedMonth?.format('MMMM YYYY') ?? '';

  const handleExport = () => {
    if (!salaryHistory.length) {
      message.warning('Nema podataka za izvoz.');
      return;
    }

    const employeeName = userDetails?.user
      ? `${userDetails.user.firstName || ''} ${userDetails.user.lastName || ''}`.trim()
      : 'Zaposleni';

    const headers = [
      'Mesec',
      'Fiksna zarada',
      'Dnevnice/Sati',
      'Procenat',
      'Bonus',
      'Prekovremeni',
      'Penali',
      'Putni troškovi',
      'Pozajmice',
      'Bruto',
      'Neto ukupno',
      'Na racun',
      'Gotovina',
    ];

    const rows = [...salaryHistory]
      .sort((a: any, b: any) => a.month.localeCompare(b.month))
      .map((p: any) => {
        const c = p.components || {};
        return [
          p.month ?? '',
          c.fixed ?? 0,
          c.daily ?? 0,
          c.percentage ?? 0,
          c.bonus ?? 0,
          c.overtime ?? 0,
          c.penalty ?? 0,
          c.travel ?? 0,
          c.loans ?? 0,
          p.bruto ?? 0,
          p.total ?? 0,
          p.payoutToBank ?? 0,
          p.cashAmount ?? 0,
        ];
      });

    const csvContent = [
      [`Obracun plate — ${employeeName}`],
      [],
      headers,
      ...rows,
    ]
      .map((row) =>
        row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(';')
      )
      .join('\r\n');

    // BOM for Excel / Cyrillic
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plate_${employeeName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="up-pay-wrap">
      {/* ── TOOLBAR ─────────────────────────────────────────────────── */}
      <div className="up-pay-toolbar">
        {/* Period nav */}
        <div className="up-pay-period">
          <button onClick={handlePrevMonth} title="Prethodni mesec">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M15 6l-6 6 6 6"/></svg>
          </button>
          <div className="up-range">
            <span className="up-range-dates">{rangeStart} <span className="up-range-arrow">→</span> {rangeEnd}</span>
            <span className="up-range-month">{monthLabel}</span>
          </div>
          <button onClick={handleNextMonth} title="Sledeći mesec">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9 6l6 6-6 6"/></svg>
          </button>
        </div>

        {/* Segment tabs + location */}
        <div className="up-pay-middle">
          <div className="up-seg">
            <button
              className={activeSegment === 'monthly' ? 'active' : ''}
              onClick={() => handleSegmentChange('monthly')}
            >Mesečno</button>
            <button
              className={activeSegment === 'first-half' ? 'active' : ''}
              onClick={() => handleSegmentChange('first-half')}
            >1–15</button>
            <button
              className={activeSegment === 'second-half' ? 'active' : ''}
              onClick={() => handleSegmentChange('second-half')}
            >16–kraj</button>
            <button
              className={activeSegment === 'custom' ? 'active' : ''}
              onClick={() => setActiveSegment('custom')}
            >Prilagođeno</button>
          </div>
          {activeSegment === 'custom' && (
            <RangePicker
              value={dateRange}
              onChange={onRangeChange}
              format="DD.MM.YYYY"
              size="small"
              style={{ marginLeft: 8 }}
            />
          )}

          {userWorkLocations.length > 0 && (
            <Select
              style={{ minWidth: 160 }}
              placeholder={t("All Locations")}
              value={selectedLocationId}
              onChange={(value) => setSelectedLocationId(value)}
              size="small"
              className="up-loc-select"
            >
              <Select.Option key="all" value={null}>{t("All Locations")}</Select.Option>
              {userWorkLocations.map((loc: any) => (
                <Select.Option key={loc._id} value={loc._id}>{loc.name}</Select.Option>
              ))}
            </Select>
          )}
        </div>

        {/* Custom range picker — hidden until re-enabled */}

        {/* Export button */}
        <button className="up-btn-ghost" onClick={handleExport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M12 4v12M6 12l6 6 6-6"/></svg>
          Izvezi
        </button>
      </div>
      <SalarySection
        employeeEarnings={
          employeeEarnings?.filteredUserPayments ?? employeeEarnings
        }
        salarySummary={employeeEarnings?.salarySummary}
        selectedLocationId={selectedLocationId}
        userWorkLocations={userWorkLocations}
        openSalaryModal={() => setUserSalaryModalOpen(true)}
        openPercentageSalaryModal={() => setPercentageSalaryModalOpen(true)}
        openBonusPaymentModal={() => setBonusPaymentModalOpen(true)}
        openPenaltyPaymentModal={() => setPenaltyModalOpen(true)}
        openLoanPaymentModal={() => setLoanPaymentModalOpen(true)}
        openShortageOverageModal={() => setShortageOverageModalOpen(true)}
        selectedMonth={selectedMonth}
        userData={userDetails}
        showDailyWageTable={(value: any) => {
          setDailyWageTableVisible(value);
          setIsPercentageSalaryTableVisible(false);
        }}
        isDetailyWageTableVisible={isDailyWageTableVisible}
        isPercentageSalaryTableVisible={isPercentageSalaryTableVisible}
        showPercentageSalaryTable={(value: any) => {
          setIsPercentageSalaryTableVisible(value);
          setDailyWageTableVisible(false);
        }}
        openOvertimePaymentModal={() => setOvertimePaymentModalOpen(true)}
        dailyRevenue={employeeEarnings?.filteredDailyRevenue}
        isTravelExpensesModalOpen={isTravelExpensesModalOpen}
        openTravelExpensesModal={() => {
          setTravelExpensesModalOpen(true);
        }}
        salaryHistory={salaryHistory}
        onOvertimeSettingsChanged={() => getEmployeeTotalEarning()}
      />
      {/* <div className="overtimeEarnings">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card bordered={false}>
              <Statistic
                title={`${t("Total Overtime Hours")} `}
                value={
                  calculateDetailedPayments(
                    userOvertimePayment,
                  ).totalHours.toFixed(1) ?? 0
                }
                suffix={t("Hours")}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false}>
              <Statistic
                title={`${t("Total earnings for overtime work")}`}
                value={
                  calculateDetailedPayments(
                    userOvertimePayment,
                  ).totalPay.toFixed(1) ?? 0
                }
                suffix={calculateCurrency()}
                valueStyle={{ color: "#3f8600" }}
                precision={2}
              />
            </Card>
          </Col>
        </Row>
      </div> */}
      {/* <Row>
        <Col xs={24}>
          {userDetails?.user?.workingPatternId?.type === "HOURLY" ? (
            <>
              <Row gutter={16} className="paymentResults">
                <Col xs={24} md={8} sm={24}>
                  <Card bordered={false}>
                    <Statistic
                      title={t("Total hours")}
                      value={Number(
                        calculateMonthlyHours(
                          userDetails?.user?.workingPatternId?.weekdays,
                          selectedMonth,
                        ),
                      )}
                      precision={1}
                      valueStyle={{ color: "#3f8600" }}
                      prefix={<ArrowUpOutlined />}
                      //   suffix={t("Hours")}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8} sm={24}>
                  <Card bordered={false}>
                    <Statistic
                      title={t("Hourly rate")}
                      value={Number(userDetails?.user.amount)}
                      precision={1}
                      valueStyle={{
                        color: "#3f8600",
                      }}
                      // prefix={renderNeedToPaidIcon()}
                      suffix={calculateCurrency()}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8} sm={24}>
                  <Card bordered={false}>
                    <Statistic
                      title={t("Earnings without overtime")}
                      value={
                        Number(userDetails?.user.amount) *
                        Number(
                          calculateMonthlyHours(
                            userDetails?.user?.workingPatternId?.weekdays,
                            selectedMonth,
                          ),
                        )
                      }
                      precision={1}
                      valueStyle={{
                        color: "#3f8600",
                      }}
                      // prefix={renderNeedToPaidIcon()}
                      suffix={calculateCurrency()}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          ) : userDetails?.user?.workingPatternId?.type === "FIXED" ? (
            <>
              <Row gutter={16} className="paymentResults">
                <Col xs={24} md={24} sm={24}>
                  <Card bordered={false}>
                    <Statistic
                      title={t("Monthly salary")}
                      value={Number(userDetails?.user.amount)}
                      precision={1}
                      valueStyle={{
                        color: "#3f8600",
                      }}
                      // prefix={renderNeedToPaidIcon()}
                      suffix={calculateCurrency()}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <></>
          )}
        </Col>
      </Row> */}
      {userDetails?.user?.workingPatternId?.type === "PERCENT" ? (
        <>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card bordered={false}>
                <Statistic
                  title={t("Total Earnings")}
                  value={totalEarned}
                  precision={1}
                  valueStyle={{ color: "#3f8600" }}
                  prefix={<ArrowUpOutlined />}
                  suffix={calculateCurrency()}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false}>
                <Statistic
                  title={
                    t("Earnings of the employee") +
                    ` ${userDetails?.user?.amount ?? 30}%`
                  }
                  value={calculatePercentage(totalEarned)}
                  precision={1}
                  valueStyle={{ color: "#3f8600" }}
                  prefix={<ArrowUpOutlined />}
                  suffix={calculateCurrency()}
                />
              </Card>
            </Col>
          </Row>
          {/* <div className="userPaymentList">
        {paymentDetails?.map((item: any) => {
          return (
            <div className="list">
              <div className="payment">
                <div className="icon">
                  <CalendarOutlined />
                </div>
                <div className="date">
                  {format(new Date(item.date), "d-MMM-yyyy")}
                </div>
                <div className="title">
                  <div>{item.title}</div>
                </div>
              </div>
              <div className="costs">{item.costs}</div>
            </div>
          );
        })}
      </div> */}
          <div className="mobile-table-wrapper">
            <Table columns={columns} dataSource={paymentDetails} />
          </div>
        </>
      ) : (
        <></>
      )}
      {fixedPaymentModalOpen && (
        <FixedPayment
          isOpen={fixedPaymentModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments ? "edit" : "create"}
          onCloseModal={() => setFixedPaymentModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
        />
      )}
      {percentageSalaryModalOpen && (
        <PercentageSalary
          isOpen={percentageSalaryModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments ? "edit" : "create"}
          onCloseModal={() => setPercentageSalaryModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
        />
      )}
      {bonusPaymentModalOpen && (
        <BonusPayment
          isOpen={bonusPaymentModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments?._id ? "edit" : "create"}
          onCloseModal={() => setBonusPaymentModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
          userWorkLocations={userWorkLocations}
          defaultLocationId={selectedLocationId}
        />
      )}
      {overtimePaymentModalOpen && (
        <OvertimePayment
          isOpen={overtimePaymentModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments?._id ? "edit" : "create"}
          onCloseModal={() => setOvertimePaymentModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
          userWorkLocations={userWorkLocations}
          defaultLocationId={selectedLocationId}
        />
      )}
      {penaltyModalOpen && (
        <PenaltyPayment
          isOpen={penaltyModalOpen}
          onCloseModal={() => setPenaltyModalOpen(false)}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments?._id ? "edit" : "create"}
          selectedMonth={selectedMonth}
          modalData={employeeEarnings?.filteredUserPayments}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
          userWorkLocations={userWorkLocations}
          defaultLocationId={selectedLocationId}
        />
      )}

      {loanPaymentModalOpen && (
        <LoanPaymentModal
          isOpen={loanPaymentModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments?._id ? "edit" : "create"}
          onCloseModal={() => setLoanPaymentModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
          userWorkLocations={userWorkLocations}
          defaultLocationId={selectedLocationId}
        />
      )}

      {shortageOverageModalOpen && (
        <ShortageOverageModal
          isOpen={shortageOverageModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments?._id ? "edit" : "create"}
          onCloseModal={() => setShortageOverageModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
          userWorkLocations={userWorkLocations}
          defaultLocationId={selectedLocationId}
        />
      )}

      {isUserSalaryModalOpen && (
        <UserHourlyRatePercentageModal
          isOpen={isUserSalaryModalOpen}
          onCloseModal={() => setUserSalaryModalOpen(false)}
          userId={params.id}
          userData={userDetails?.user}
          onUpdate={() => {
            getEmployeeTotalEarning();
            onUpdateUser?.();
          }}
        />
      )}

      {isTravelExpensesModalOpen && (
        <TravelExpensesModal
          isOpen={isTravelExpensesModalOpen}
          modalData={employeeEarnings?.filteredUserPayments}
          selectedMonth={selectedMonth}
          userId={params.id}
          mode={employeeEarnings?.filteredUserPayments ? "edit" : "create"}
          onCloseModal={() => setTravelExpensesModalOpen(false)}
          onUpdate={() => {
            getEmployeeTotalEarning();
          }}
        />
      )}
    </div>
  );
};

export default UserPayment;
